import React, { useState } from 'react';
import { Clock, Play, Pause, Plus, DollarSign, Calendar, CheckCircle2 } from 'lucide-react';

export default function TimeTracking({ timeEntries, onAddTimeEntry, projects, team }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(projects[0]?.id || '');
  const [taskTitle, setTaskTitle] = useState('');
  const [hours, setHours] = useState(4);
  const [isBillable, setIsBillable] = useState(true);

  const totalHours = timeEntries.reduce((acc, curr) => acc + curr.hours, 0);
  const billableHours = timeEntries.filter(t => t.billable).reduce((acc, curr) => acc + curr.hours, 0);

  const handleAddTime = (e) => {
    e.preventDefault();
    if (!taskTitle.trim()) return;

    const newTe = {
      id: 'te-' + Date.now(),
      userId: team[0]?.id || 'usr-1',
      projectId: selectedProject,
      taskTitle: taskTitle.trim(),
      hours: Number(hours),
      date: new Date().toISOString().split('T')[0],
      billable: isBillable
    };

    onAddTimeEntry(newTe);
    setShowAddModal(false);
    setTaskTitle('');
  };

  return (
    <div className="p-8 space-y-8 animate-fadeIn">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-violet-900 to-slate-900 rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-700/60 rounded-full text-xs font-medium text-indigo-200">
            <Clock className="w-3.5 h-3.5 text-indigo-300" /> Timesheet & Billing
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Time Tracking & Billable Hours</h1>
          <p className="text-indigo-200 text-sm max-w-xl">
            Track billable hours, review project labor costs, and log timesheets effortlessly across all active sprints.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-5 py-3 bg-white text-indigo-900 hover:bg-indigo-50 rounded-xl font-semibold text-sm transition shadow-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Log Time Entry
        </button>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Hours Logged</span>
          <h3 className="text-3xl font-extrabold text-slate-900">{totalHours} hrs</h3>
          <p className="text-xs text-slate-500">Across {timeEntries.length} time logs</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Billable Hours</span>
          <h3 className="text-3xl font-extrabold text-emerald-600">{billableHours} hrs</h3>
          <p className="text-xs text-slate-500">{Math.round((billableHours / (totalHours || 1)) * 100)}% of total effort</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Estimated Revenue</span>
          <h3 className="text-3xl font-extrabold text-indigo-600">${billableHours * 150}</h3>
          <p className="text-xs text-slate-500">At standard $150/hr rate</p>
        </div>
      </div>

      {/* Time Entries Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Recent Time Logs</h2>
            <p className="text-xs text-slate-500">Detailed record of hours spent on tasks</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <th className="p-4">Team Member</th>
                <th className="p-4">Project</th>
                <th className="p-4">Task Description</th>
                <th className="p-4">Hours</th>
                <th className="p-4">Date</th>
                <th className="p-4">Billing Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs font-medium">
              {timeEntries.map(te => {
                const member = team.find(m => m.id === te.userId);
                const proj = projects.find(p => p.id === te.projectId);
                return (
                  <tr key={te.id} className="hover:bg-slate-50/80 transition">
                    <td className="p-4 flex items-center space-x-2">
                      {member && <img src={member.avatar} alt={member.name} className="w-6 h-6 rounded-full object-cover" />}
                      <span className="font-bold text-slate-900">{member?.name}</span>
                    </td>
                    <td className="p-4 font-semibold text-indigo-600">{proj?.name}</td>
                    <td className="p-4 text-slate-800">{te.taskTitle}</td>
                    <td className="p-4 font-mono font-bold text-slate-900">{te.hours} hrs</td>
                    <td className="p-4 font-mono text-slate-500">{te.date}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${te.billable ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {te.billable ? 'Billable' : 'Non-Billable'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Time Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-6">
            <h2 className="text-lg font-bold text-slate-900">Log Time Entry</h2>
            
            <form onSubmit={handleAddTime} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Project</label>
                <select 
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                >
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.key})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Task Title / Activity</label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. Code review & bug fixing"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Hours</label>
                  <input 
                    type="number"
                    min={0.5}
                    max={24}
                    step={0.5}
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Billing Type</label>
                  <select 
                    value={isBillable ? 'yes' : 'no'}
                    onChange={(e) => setIsBillable(e.target.value === 'yes')}
                    className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                  >
                    <option value="yes">Billable</option>
                    <option value="no">Non-Billable</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end space-x-3">
                <button 
                  type="button" 
                  onClick={() => setShowAddModal(false)}
                  className="px-5 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 shadow-md shadow-indigo-600/20"
                >
                  Save Time Log
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
